import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';

function ETLAgent() {
  return (
    <>
      <SEO pathname="/agents/etl-pipeline" agentKey="ETLAgent" />

    <main className="agent-page" style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--color-bg-light)', color: 'var(--color-text-primary)' }}>
      {/* Hero Section */}
      <section style={{ padding: '60px 0', background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, transparent 100%)' }}>
        <div className="container">
          <div style={{ marginBottom: '2rem' }}>
            <Link to="/" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span>←</span> Back to Home
            </Link>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '48px', alignItems: 'center' }}>
            <div className="waas-card" style={{ flex: '1', minWidth: '300px', maxWidth: '400px', margin: '0', background: 'white', padding: '40px', borderRadius: '24px', boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: 'var(--color-primary)', filter: 'blur(80px)', opacity: '0.2', borderRadius: '50%' }}></div>
              <div className="waas-icon-container" style={{ margin: '0 auto 24px auto', width: '140px', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-subtle)', borderRadius: '50%' }}>
                 <img src="/images/icons/logo_computer.webp" alt="ETL Data Engineer Agent" width="100" height="100" style={{ objectFit: 'contain' }} />
              </div>
              <h1 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '8px', color: '#1d1d1f' }}>ETL Agent</h1>
              <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', marginBottom: '24px', fontSize: '1.1rem' }}>Two-Stage Pipeline Compiler</p>
              
              <div style={{ background: 'rgba(0,200,83,0.1)', color: '#00c853', padding: '8px 16px', borderRadius: '100px', display: 'inline-block', fontWeight: '600', fontSize: '0.9rem', width: '100%', textAlign: 'center', marginBottom: '16px' }}>
                ● Status: Online & Ready
              </div>
              <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#86868b', marginBottom: '32px' }}>Supported: Databricks, Snowflake, BigQuery, Redshift</p>
              
              <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20ETL%20Data%20Agent" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', textAlign: 'center', display: 'block', padding: '16px 0', fontSize: '1.1rem', textDecoration: 'none' }}>
                Request Access Now
              </a>
            </div>

            <div style={{ flex: '2', minWidth: '400px' }}>
              <span className="section-label">Digital Workforce Persona</span>
              <h2 style={{ fontSize: '3rem', marginBottom: '24px', color: '#1d1d1f', fontWeight: '700', lineHeight: '1.2' }}>Your Principal Data Engineer.</h2>
              <p style={{ fontSize: '1.25rem', color: '#424245', marginBottom: '24px', lineHeight: '1.6' }}>
                A hyper-intelligent conversational agent designed to construct production-ready data pipelines via an advanced two-stage hybrid compiler architecture. 
              </p>
              <p style={{ fontSize: '1.1rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Say goodbye to hallucinatory SQL code generation. Using FinOps shift-left principles, the agent first functions as a conversational auditor—profiling raw data tables natively inside your warehouse to infer grain, primary keys, and schema specs. Second, it shifts the generated structured <code>PipelineSpec</code> into a purely deterministic code compiler to generate absolute mathematically sound PySpark, dlt, or dbt code with zero risks of hallucination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Capabilities Section */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span className="section-label">Agent Capabilities</span>
            <h2 className="section-title">Flawless Pipeline Orchestration</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px' }}>
            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Deep Warehouse Profiling</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Executes <code>profile_tables</code> to inspect source schema and data profiles directly inside the warehouse. It infers Primary Key combinations, identifies partition opportunities, and spots Personally Identifiable Information (PII) before a line of code is written.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Deterministic Compiler</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Stage 1 relies on the LLM conversational intelligence to define requirements. But Stage 2 (code-generation) is strictly guarded and template-driven. This ensures outputs like DDLs, monitoring triggers, or PySpark scripts execute 100% reliably.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Data Quality Implementation</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Configures Data Quality (DQ) hard and soft rules inside the data contract organically while compiling. Drops warnings or quarantines anomalies seamlessly based on FinOps best practices parameters.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Incremental Extraction Logic</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Programmed to challenge expensive full-refresh models. Evaluates target grain and SCD types dynamically to configure complex Watermark appending merges natively inside Data Lake architectures.
              </p>
            </div>
            
            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Automatic Runbook Output</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Along with executing the transformations, the deterministic compiler pumps out required artifacts—generating comprehensive runbooks, CI/CD specifications, DAGs, and backfill scripts seamlessly.
              </p>
            </div>

            <div style={{ padding: '32px', borderRadius: '24px', background: 'var(--color-bg-light)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '16px', color: '#1d1d1f' }}>Multi-Warehouse Execution</h3>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Fully integrated with Snowflake, Databricks, BigQuery, and Redshift. Translates semantic target types mapping syntax safely across disparate warehouse routing models using abstract layers.
              </p>
            </div>
          </div>
        </div>
      </section>
      <FAQSection agentKey="ETLAgent" />
    </main>
    </>
  );
}

export default ETLAgent;
